/**
 * The wire page size, not the display page size.
 *
 * 1000 is apps-api's own cap and its own default for `limit` (`joi.number().max(1000)`), so this asks
 * for the largest page the service will give. It is also why an *unpaged* call is a silent bug rather
 * than a slow one: the service answers with the first 1000 rows and says nothing about the rest.
 */
const FETCH_PAGE_SIZE = 1000;

/**
 * A hard stop on the paging walk, counted in rows rather than in requests.
 *
 * - The walk exists only because `limit` is capped: a collection past 1000 needs a second request.
 * - The cap is here so a backend that ignores `offset` — and so returns a full page forever — fails
 *   loudly instead of looping until the tab dies.
 * - Counted in rows because the walk needs one request *past* the last full page to learn it has
 *   ended. A budget counted in requests makes a collection of exactly the cap the one size that errors.
 */
const MAX_ROWS = 20_000;

/**
 * Walks an apps-api collection to the end.
 *
 * - Every list endpoint here takes the same `{ offset, limit }` and returns a bare array, so one walk
 *   covers components, developers and vendors alike.
 * - `subject` names the collection in the give-up error, which is the only place callers differ.
 * - The caller closes over its own `signal`.
 */
export const fetchAllPages = async <Row>(
  fetchPage: (range: { offset: number; limit: number }) => Promise<Row[]>,
  subject: string,
): Promise<Row[]> => {
  const all: Row[] = [];

  for (let offset = 0; offset <= MAX_ROWS; offset += FETCH_PAGE_SIZE) {
    const batch = await fetchPage({ offset, limit: FETCH_PAGE_SIZE });
    all.push(...batch);
    // A short page is the last page.
    if (batch.length < FETCH_PAGE_SIZE) return all;
  }

  throw new Error(
    `Stopped paging ${subject} past ${MAX_ROWS} rows — the API kept returning full pages, which suggests 'offset' is being ignored.`,
  );
};
